/**
 * Widget V2 — Service detail (discovery)
 *
 * Built around structured facts rather than prose, so the page holds up even
 * when the club wrote no description: hero photo (when uploaded), at-a-glance
 * facts, a generated blurb fallback, tappable duration pricing that deep-links
 * into booking, location photo cards and a coach rail. CTA is sticky on mobile.
 */

import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import { useWidget } from "/imports/state/WidgetContext";
import { EmptyState, useImageFallback } from "/imports/ui/components/shared";
import { getServiceDurationOptions } from "/imports/ui/components/booking";
import {
  CoachAvatar,
  fullName,
  PageHeading,
  bookingHref,
  SECTION_LABEL_SX,
  DISCOVERY_CARD_SX,
  CATEGORY_ROUTE
} from "/imports/ui/components/discovery";

// When the club wrote no description, compose one from what we know — the page
// should never open with a blank "About".
function generatedBlurb(service, sport, coachCount, locationCount) {
  const sportBit = sport?.name ? `${sport.name.toLowerCase()} ` : "";
  switch (service.categoryType) {
    case "category-service-private":
      return `One-on-one ${sportBit}coaching tailored to your level${coachCount > 1 ? `, with ${coachCount} coaches to choose from` : ""}.`;
    case "category-service-court":
      return `Reserve a ${sportBit}court${locationCount > 1 ? ` at any of our ${locationCount} locations` : ""} and play at your preferred time.`;
    case "category-service-class":
      return `Group ${sportBit}sessions led by our coaches.`;
    case "category-service-term":
      return `A structured multi-week ${sportBit}program to build your skills.`;
    default:
      return "";
  }
}

function Fact({ icon: Icon, children }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Icon sx={{ fontSize: "1.125rem", color: "primary.main", flexShrink: 0 }} />
      <Typography variant="body2" fontWeight={600} noWrap>
        {children}
      </Typography>
    </Box>
  );
}

function LocationThumb({ src, name }) {
  const { showImage, onImageError } = useImageFallback(src);

  if (showImage) {
    return (
      <Box
        component="img"
        src={src}
        alt={name}
        onError={onImageError}
        sx={{ width: "100%", height: "5.25rem", borderRadius: 3, objectFit: "cover", display: "block" }}
      />
    );
  }

  return (
    <Box
      sx={{
        height: "5.25rem",
        borderRadius: 3,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
        color: "primary.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <PlaceOutlinedIcon />
    </Box>
  );
}

function ServiceDetailView() {
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const { slug, services, sports, locations, trainers, currency } = useWidget();

  const service = useMemo(() => (services || []).find((s) => s._id === serviceId), [services, serviceId]);
  const { showImage: showHero, onImageError: onHeroError } = useImageFallback(service?.imgUrl);

  const sport = useMemo(() => (sports || []).find((s) => s._id === service?.sportId), [sports, service]);
  const coaches = service?.trainers || [];
  // Service-embedded trainers don't carry the resolved photo URL — look it up
  // from the top-level trainers list (which does).
  const coachImg = (id) => (trainers || []).find((t) => t._id === id)?.imgUrl;
  const serviceLocations = useMemo(() => (locations || []).filter((l) => (service?.locationIds || []).includes(l._id)), [locations, service]);

  if (!service) {
    return (
      <Box>
        <PageHeading title="Service" onBack={() => navigate(`/${slug}/services`)} />
        <EmptyState title="Service not found" actionLabel="Back to services" onAction={() => navigate(`/${slug}/services`)} />
      </Box>
    );
  }

  const levels = service.levelRequirements || [];
  const route = CATEGORY_ROUTE[service.categoryType];
  const canBook = Boolean(route);
  // Courts & lessons let the visitor pick a duration, so their price rows can
  // deep-link with it pre-set; classes/terms have fixed session lengths.
  const durationPresets = route === "courts" || route === "lessons";
  const durationOptions = getServiceDurationOptions(service);
  const prices = durationOptions.map((o) => o.price).filter((p) => p != null);
  const minPrice = prices.length ? Math.min(...prices) : null;
  const isFrom = prices.length > 1 || durationOptions.some((o) => o.isFrom);
  const durationsLabel = durationOptions.length ? durationOptions.map((o) => o.value).join(" / ") + " min" : null;

  const about = service.description || generatedBlurb(service, sport, coaches.length, serviceLocations.length);

  const facts = [
    durationsLabel && { key: "duration", icon: AccessTimeIcon, text: durationsLabel },
    minPrice != null && { key: "price", icon: SellOutlinedIcon, text: `${isFrom ? "from " : ""}${currency} ${minPrice}` },
    serviceLocations.length > 0 && {
      key: "location",
      icon: PlaceOutlinedIcon,
      text: serviceLocations.length === 1 ? serviceLocations[0].name : `${serviceLocations.length} locations`
    },
    coaches.length > 0 && { key: "coaches", icon: GroupsOutlinedIcon, text: `${coaches.length} coach${coaches.length > 1 ? "es" : ""}` }
  ].filter(Boolean);

  return (
    <Box>
      <PageHeading title={service.name} subtitle={service.categoryName || undefined} onBack={() => navigate(`/${slug}/services`)} />

      <Box sx={{ ...DISCOVERY_CARD_SX, overflow: "hidden" }}>
        {showHero && (
          <Box
            component="img"
            src={service.imgUrl}
            alt={service.name}
            onError={onHeroError}
            sx={{ width: "100%", height: { xs: 170, md: 240 }, objectFit: "cover", display: "block" }}
          />
        )}

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {sport && (
              <Chip label={sport.name} sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: "primary.main", fontWeight: 600 }} />
            )}
            {levels.length > 0 ? (
              <Chip variant="outlined" label={`Suitable for levels ${levels.join(", ")}`} />
            ) : (
              <Chip variant="outlined" label="All levels welcome" />
            )}
          </Box>

          {facts.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 1, mt: 2 }}>
              {facts.map((f) => (
                <Fact key={f.key} icon={f.icon}>
                  {f.text}
                </Fact>
              ))}
            </Box>
          )}

          {about && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ ...SECTION_LABEL_SX, mb: 1 }}>About this service</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>
                {about}
              </Typography>
            </Box>
          )}

          {durationOptions.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ ...SECTION_LABEL_SX, mb: 1 }}>Pricing</Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {durationOptions.map((o) => (
                  <Box
                    key={o.value}
                    onClick={durationPresets && canBook ? () => navigate(bookingHref(slug, service, { duration: o.value })) : undefined}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 3,
                      px: 2,
                      py: 1.25,
                      minWidth: "6.875rem",
                      ...(durationPresets && canBook
                        ? { cursor: "pointer", transition: "border-color 0.15s ease", "&:hover": { borderColor: "primary.main" } }
                        : {})
                    }}
                  >
                    <Typography fontWeight={700} sx={{ lineHeight: 1.3 }}>
                      {o.price != null ? `${o.isFrom ? "from " : ""}${currency} ${o.price}` : "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {serviceLocations.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ ...SECTION_LABEL_SX, mb: 1 }}>Available at</Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  overflowX: "auto",
                  pb: 0.5,
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none"
                }}
              >
                {serviceLocations.map((l) => (
                  <Box
                    key={l._id}
                    onClick={() => navigate(`/${slug}/locations/${l._id}`)}
                    sx={{ width: "9.375rem", flexShrink: 0, cursor: "pointer", "&:hover .loc-name": { color: "primary.main" } }}
                  >
                    <LocationThumb src={l.imgUrls?.[0]} name={l.name} />
                    <Typography className="loc-name" variant="body2" fontWeight={600} noWrap sx={{ mt: 0.75 }}>
                      {l.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Desktop CTA closes the card at the bottom-left, in the content's
              reading column; mobile gets the sticky bar below instead. */}
          {canBook && (
            <Box sx={{ mt: 3, display: { xs: "none", md: "flex" } }}>
              <Button variant="contained" size="large" onClick={() => navigate(bookingHref(slug, service))} sx={{ color: "common.white" }}>
                Book now
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {coaches.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>Coaches</Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              overflowX: "auto",
              pb: 0.5,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none"
            }}
          >
            {coaches.map((c) => (
              <Box
                key={c._id}
                onClick={() => navigate(canBook ? bookingHref(slug, service, { trainerId: c._id }) : `/${slug}/coaches/${c._id}`)}
                sx={{
                  ...DISCOVERY_CARD_SX,
                  p: 1.5,
                  width: "6.75rem",
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "border-color 0.15s ease",
                  "&:hover": { borderColor: "primary.main" }
                }}
              >
                <CoachAvatar trainer={c} size={56} src={coachImg(c._id)} />
                <Typography variant="body2" fontWeight={600} noWrap sx={{ mt: 1, maxWidth: "100%" }}>
                  {fullName(c)}
                </Typography>
                {c.title && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: "100%" }}>
                    {c.title}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Mobile CTA — sticky so it floats above the bottom nav until the reader
          reaches the end of the page. */}
      {canBook && (
        <Box sx={{ mt: 3, display: { md: "none" }, position: "sticky", bottom: 72, zIndex: 5 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate(bookingHref(slug, service))}
            sx={{ py: 1.25, color: "common.white", boxShadow: "0 6px 20px rgba(26,26,26,0.18)" }}
          >
            Book {service.name}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default ServiceDetailView;
